import React, { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import Home from '@components/Home'
import PrivateRoute from '@components/PrivateRoute'
import useSignInHandler from '@components/SignInHandler'
import SubscriptionRoute from '@components/SubscriptionRoute'
import CommonLayout from '@components/layouts/CommonLayout'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { usePendoIntegration } from '@modules/auth/hooks/usePendoIntegration'
import { userSubscriptionLevel } from '@modules/auth/types'
import { ChatProvider } from '@modules/chat/useChatContext'
import HubPage from '@modules/hub'
import Profile from '@modules/profile'
import Search from '@modules/search'
import SettingsPage from '@modules/settings'
import ShortlistPage from '@modules/shortlist'
import AlertsPage from '@modules/updates'
import { clearUrlDataFromLocalStorage, retrieveUrlDataFromLocalStorage } from '@shared/utils'

import { logPageView } from './ga'
import useToggleLocalization from './i18n/useToggleLocalization'

const App = () => {
  const { isAuthenticated, isFetching = true } = useAuthContext()

  useToggleLocalization()

  const location = useLocation()

  useSignInHandler()
  usePendoIntegration()

  useEffect(() => {
    logPageView()
  }, [location])

  useEffect(() => {
    const { pathname } = retrieveUrlDataFromLocalStorage()

    if (isAuthenticated && pathname === location.pathname) {
      clearUrlDataFromLocalStorage()
    }
  }, [isAuthenticated, location.pathname])

  return !isFetching ? (
    <Routes>
      <Route
        path="/plus"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <SubscriptionRoute
              accessibleLevels={[
                userSubscriptionLevel.SUBSCRIBED_USER,
                userSubscriptionLevel.ECOMMERCE,
                userSubscriptionLevel.VENDOR,
              ]}
            >
              <ChatProvider>
                <Home />
              </ChatProvider>
            </SubscriptionRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/plus/hub"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <ChatProvider>
              <HubPage />
            </ChatProvider>
          </PrivateRoute>
        }
      />
      <Route element={<CommonLayout />}>
        <Route
          path="/plus/search"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <SubscriptionRoute
                accessibleLevels={[
                  userSubscriptionLevel.SUBSCRIBED_USER,
                  userSubscriptionLevel.ECOMMERCE,
                  userSubscriptionLevel.VENDOR,
                ]}
              >
                <ChatProvider>
                  <Search />
                </ChatProvider>
              </SubscriptionRoute>
            </PrivateRoute>
          }
        />
      </Route>

      <Route element={<CommonLayout />}>
        <Route
          path="/plus/custom-list"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <SubscriptionRoute
                accessibleLevels={[
                  userSubscriptionLevel.SUBSCRIBED_USER,
                  userSubscriptionLevel.ECOMMERCE,
                  userSubscriptionLevel.VENDOR,
                ]}
              >
                <ChatProvider>
                  <ShortlistPage />
                </ChatProvider>
              </SubscriptionRoute>
            </PrivateRoute>
          }
        />
      </Route>

      <Route element={<CommonLayout />}>
        <Route
          path="/plus/alerts/:tab"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <SubscriptionRoute
                accessibleLevels={[
                  userSubscriptionLevel.SUBSCRIBED_USER,
                  // userSubscriptionLevel.ECOMMERCE,
                  userSubscriptionLevel.VENDOR,
                ]}
              >
                <ChatProvider>
                  <AlertsPage />
                </ChatProvider>
              </SubscriptionRoute>
            </PrivateRoute>
          }
        />
      </Route>

      <Route element={<CommonLayout />}>
        <Route
          path="/plus/settings"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <ChatProvider>
                <SettingsPage />
              </ChatProvider>
            </PrivateRoute>
          }
        />
      </Route>

      <Route element={<CommonLayout />}>
        <Route
          path="/plus/profile/:profileId/*"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <ChatProvider>
                <Profile />
              </ChatProvider>
            </PrivateRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/plus/hub" replace />} />
    </Routes>
  ) : null
}

export default App
